/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import axios from 'axios';

const META_ERROR_PREFIX = 'meta_error: ';

/** Reasons the backend reports when a requested Lead Ad could not even be attempted. */
export type LeadAdErrorCode =
  | 'ad_account_missing'
  | 'facebook_not_connected'
  | 'leadgen_terms_not_accepted'
  | 'budget_below_minimum'
  | 'form_question_prohibited';

/**
 * The smallest daily budget, in reais, the ad dialogs accept. Meta rejects an
 * ad set whose daily budget is not above roughly one US dollar (R$ 5.27 when
 * this was found), and that figure moves with the exchange rate, so this keeps
 * a margin above it. Meta's own rejection is still recognized as a fallback.
 */
export const MIN_AD_DAILY_BUDGET_REAIS = 6;

/**
 * Whether a daily budget (in reais) is below what Meta accepts. Not-a-number
 * counts as below, so an empty field cannot slip through.
 *
 * @param reais - The daily budget in reais.
 * @returns True when it must be raised before an ad can be created.
 */
export function isBelowMinimumAdBudget(reais: number): boolean {
  return !(reais >= minAdDailyBudgetReais());
}

/**
 * The minimum Meta reports for the agency's ad account, in whole reais (0 until
 * the settings that carry it have loaded). It only ever raises the floor above
 * {@link MIN_AD_DAILY_BUDGET_REAIS}; the server enforces it either way.
 */
let metaMinDailyBudgetReais = 0;

/**
 * Remembers the minimum daily budget Meta reported for the account, from the
 * agency settings. Anything that is not a positive number clears it.
 *
 * @param cents - `ad_min_daily_budget_cents` from the settings response.
 */
export function setMetaMinDailyBudgetCents(cents: unknown): void {
  metaMinDailyBudgetReais = typeof cents === 'number' && Number.isFinite(cents) && cents > 0 ? Math.ceil(cents / 100) : 0;
}

/**
 * The smallest daily budget, in whole reais, the ad dialogs accept right now:
 * Meta's own minimum for the account when known and higher, else the fixed floor.
 *
 * @returns Whole reais.
 */
export function minAdDailyBudgetReais(): number {
  return Math.max(MIN_AD_DAILY_BUDGET_REAIS, metaMinDailyBudgetReais);
}

/** Shortest and longest run, in days, the ad dialogs accept (the backend enforces the same range). */
export const MIN_AD_DURATION_DAYS = 1;
/** Maximum run, in days, the ad dialogs accept. */
export const MAX_AD_DURATION_DAYS = 90;

/**
 * City-targeting radius range for Lead Ads, in km. Lead Ads are Housing ads,
 * which Meta requires to use at least a 15-mile (about 24 km) radius.
 */
export const MIN_LEAD_AD_RADIUS_KM = 25;
/** Maximum targeting radius, in km, the ad dialogs accept for Housing ads. */
export const MAX_LEAD_AD_RADIUS_KM = 80;

/**
 * Clamp whole.
 */
function clampWhole(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Brings a typed daily budget (reais) up to the minimum Meta accepts. An empty
 * or invalid entry becomes the minimum, so the field can never hold a value
 * the ad would be rejected for.
 *
 * @param reais - The daily budget as typed.
 * @returns A whole number of reais, never below {@link minAdDailyBudgetReais}.
 */
export function clampAdBudgetReais(reais: number): number {
  return clampWhole(reais, minAdDailyBudgetReais(), Number.MAX_SAFE_INTEGER);
}

/**
 * Brings a typed run length into the accepted range.
 *
 * @param days - The duration as typed.
 * @returns Whole days between {@link MIN_AD_DURATION_DAYS} and {@link MAX_AD_DURATION_DAYS}.
 */
export function clampAdDurationDays(days: number): number {
  return clampWhole(days, MIN_AD_DURATION_DAYS, MAX_AD_DURATION_DAYS);
}

/**
 * Brings a typed Lead Ad targeting radius into the range Meta accepts for
 * Housing ads.
 *
 * @param km - The radius as typed.
 * @returns Whole km between {@link MIN_LEAD_AD_RADIUS_KM} and {@link MAX_LEAD_AD_RADIUS_KM}.
 */
export function clampLeadAdRadiusKm(km: number): number {
  return clampWhole(km, MIN_LEAD_AD_RADIUS_KM, MAX_LEAD_AD_RADIUS_KM);
}

/**
 * Housien's own addresses, which older versions pre-filled into the Lead Ad
 * form's privacy policy and button link. They face the agency's leads, so a
 * saved copy counts as "not set" (the backend ignores them the same way).
 */
const LEGACY_LEAD_AD_URLS: ReadonlySet<string> = new Set([
  'https://housien.com.br',
  'https://housien.com.br/privacy',
  'https://housien.app',
  'https://housien.app/privacidade',
]);

/**
 * A saved Lead Ad link with the retired Housien defaults blanked out.
 *
 * @param url - The link as stored.
 * @returns The link, or an empty string when it is one of the old defaults.
 */
export function withoutLegacyLeadAdUrl(url: string | null | undefined): string {
  const trimmed = (url ?? '').trim();
  const key = trimmed.toLowerCase().replace('://www.', '://').replace(/\/+$/, '');
  return LEGACY_LEAD_AD_URLS.has(key) ? '' : trimmed;
}

/**
 * Whether a typed link is acceptable for a Lead Ad form: empty (the default
 * applies) or an https URL. Meta rejects anything else.
 *
 * @param url - The link as typed.
 * @returns True when it can be saved.
 */
export function isValidLeadAdUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return true;
  try {
    return new URL(trimmed).protocol === 'https:';
  } catch {
    return false;
  }
}

const LEAD_AD_ERROR_CODES: ReadonlySet<string> = new Set<LeadAdErrorCode>([
  'ad_account_missing',
  'facebook_not_connected',
  'form_question_prohibited',
]);

/**
 * How Meta words "the Page has not accepted the Lead Generation Terms of
 * Service", per language (it answers in the Page admin's language, and gives
 * no machine-readable code for this case). Matched on the distinctive phrase
 * only; any other Meta message keeps the generic guidance.
 */
const BUDGET_TOO_LOW_PATTERNS: readonly RegExp[] = [
  /or[cç]amento do conjunto de an[uú]ncios deve ser superior/i,
  /ad set budget (must|should) be (more|greater|higher|above|at least)/i,
  /presupuesto del conjunto de anuncios debe ser (superior|mayor)/i,
];

const LEADGEN_TERMS_PATTERNS: readonly RegExp[] = [
  /lead\s*generation\s*terms/i,
  /termos de servi[cç]o de gera[cç][aã]o de cadastros/i,
  /generaci[oó]n de clientes potenciales/i,
];

/**
 * Recognizes the backend's "could not be attempted" reasons in a publish
 * result's `lead_ad_error`.
 *
 * @param reason - The `lead_ad_error` string from the publish result.
 * @returns The known code, or null for any other reason.
 */
export function leadAdErrorCode(reason: string | undefined | null): LeadAdErrorCode | null {
  if (!reason) return null;
  if (LEAD_AD_ERROR_CODES.has(reason)) return reason as LeadAdErrorCode;
  return reason.startsWith(META_ERROR_PREFIX) ? metaErrorCode(reason) : null;
}

/** The known case a "meta_error: ..." reason belongs to, recognized by Meta's wording; null otherwise. */
function metaErrorCode(reason: string): LeadAdErrorCode | null {
  if (LEADGEN_TERMS_PATTERNS.some((p) => p.test(reason))) return 'leadgen_terms_not_accepted';
  return BUDGET_TOO_LOW_PATTERNS.some((p) => p.test(reason)) ? 'budget_below_minimum' : null;
}

/**
 * Extracts Meta's own explanation from a publish result's `lead_ad_error`.
 * The backend prefixes the reason with "meta_error: " only when Meta's Ads API
 * rejected a step; any other reason (network failure, incomplete chain) has
 * nothing a user can act on, so it yields null.
 *
 * @param reason - The `lead_ad_error` string from the publish result.
 * @returns Meta's message, or null when there is none.
 */
export function leadAdErrorDetail(reason: string | undefined | null): string | null {
  if (!reason || !reason.startsWith(META_ERROR_PREFIX)) return null;
  const detail = reason.slice(META_ERROR_PREFIX.length).trim();
  return detail || null;
}

/**
 * The backend's machine-readable error code from a failed boost request.
 *
 * @param error - The thrown value.
 * @returns The `error` code (e.g. "boost_failed"), or undefined.
 */
export function boostErrorCode(error: unknown): string | undefined {
  return axios.isAxiosError(error) ? error.response?.data?.error : undefined;
}

/**
 * Meta's own explanation carried on a failed boost's 502 body.
 *
 * @param error - The thrown value.
 * @returns Meta's message, or null when the backend had none.
 */
export function boostErrorDetail(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const detail = error.response?.data?.error_detail;
  return typeof detail === 'string' && detail.trim() ? detail.trim() : null;
}

/**
 * Formats an amount in cents as Brazilian reais (the currency Meta bills in).
 *
 * @param cents - Amount in cents.
 * @returns A string like "R$ 15,00".
 */
export function formatBrlFromCents(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

/**
 * Maximum a campaign can spend over its whole run: daily budget × days.
 *
 * @param dailyBudgetCents - Daily budget in cents.
 * @param durationDays - Run length in days.
 * @returns Total in cents.
 */
export function totalSpendCents(dailyBudgetCents: number, durationDays: number): number {
  return dailyBudgetCents * durationDays;
}

/**
 * The most a whole spend confirmation can cost: every ad's daily budget times
 * its duration, added up.
 *
 * @param summary - The ads being confirmed.
 * @returns Total in cents.
 */
export function summaryTotalCents(summary: {
  items: ReadonlyArray<{ dailyBudgetCents: number; durationDays: number }>;
}): number {
  return summary.items.reduce(
    (sum, item) => sum + totalSpendCents(item.dailyBudgetCents, item.durationDays),
    0,
  );
}

/**
 * The day an ad that starts now stops running.
 *
 * @param from - When the ad starts.
 * @param durationDays - How many days it runs.
 * @returns The end date.
 */
export function adSpendEndDate(from: Date, durationDays: number): Date {
  const end = new Date(from.getTime());
  end.setDate(end.getDate() + durationDays);
  return end;
}

/** Meta terms the user accepts to advertise through their own ad account. */
export const META_COMMERCIAL_TERMS_URL = 'https://www.facebook.com/legal/commercial_terms';
export const META_LEAD_ADS_TERMS_URL = 'https://www.facebook.com/legal/leadgen/tos';

/** The user's confirmation of one ad's spend, as the backend expects and records it. */
export interface AdSpendConsent {
  accepted: true;
  daily_budget_cents: number;
  duration_days: number;
  total_cents: number;
}

/**
 * Builds the consent sent with a request that creates a paid ad. Only call
 * this after the user confirmed the spend dialog for exactly these amounts.
 *
 * @param dailyBudgetCents - Daily budget in cents.
 * @param durationDays - How many days the ad runs.
 * @returns The consent payload; the server rejects it unless it matches the request.
 */
export function buildAdSpendConsent(dailyBudgetCents: number, durationDays: number): AdSpendConsent {
  return {
    accepted: true,
    daily_budget_cents: dailyBudgetCents,
    duration_days: durationDays,
    total_cents: totalSpendCents(dailyBudgetCents, durationDays),
  };
}

/**
 * Link to a single ad in Meta's Ads Manager (the ads tab, with that ad
 * selected), or null when the ad account is not known.
 *
 * @param adAccountId - The ad account id, with or without the "act_" prefix.
 * @param adId - The ad's id.
 * @returns The Ads Manager URL, or null.
 */
export function adsManagerAdUrl(adAccountId: string | null | undefined, adId: string): string | null {
  const numericAccount = (adAccountId ?? '').replace(/^act_/, '').trim();
  if (!numericAccount || !adId) return null;
  return `https://adsmanager.facebook.com/adsmanager/manage/ads?act=${encodeURIComponent(numericAccount)}&selected_ad_ids=${encodeURIComponent(adId)}`;
}
